import React, { useState, useEffect } from 'react';
import './App.css';
import SignatureEditor from './components/SignatureEditor';
import SignatureAssignment from './components/SignatureAssignment';
import Dashboard from './components/Dashboard';
import SignatureList from './components/SignatureList';
import SignatureDetail from './components/SignatureDetail';
import Settings from './components/Settings';
import Overview from './components/Overview';
import AuthButton from './components/AuthButton';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';

type View = 'dashboard' | 'signatureList' | 'signatureDetail' | 'signatureEditor' | 'signatureAssignment' | 'settings' | 'overview';
type SettingsTab = 'account' | 'users' | 'deployment';

function App() {
  const [signatures, setSignatures] = useState<Array<{ id: string; name: string; html: string; assignments?: string[] }>>([]);
  const [currentSignature, setCurrentSignature] = useState<{ id: string; name: string; html: string; assignments?: string[] } | null>(null);
  const [originalAssignments, setOriginalAssignments] = useState<string[]>([]);
  const [currentView, setCurrentView] = useState<View>('dashboard');
  const [settingsTab, setSettingsTab] = useState<SettingsTab>('account');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loadingRole, setLoadingRole] = useState(true);
  const [tenant, setTenant] = useState<{ trial: boolean; enabled?: boolean; tenantCreated?: string; trialExpiration?: string } | null>(null);
  const { accounts, instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // Register tenant and load signatures when user first authenticates
  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      const account = accounts[0];
      initializeUser(account);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accounts]);

  const initializeUser = async (account: any) => {
    // Wait for user registration to complete before loading role
    await registerTenantAndUser(account.tenantId, account);
    loadUserRole(account.localAccountId, account.tenantId);
    loadTenantInfo(account.tenantId);
    loadSignatures(account.tenantId);
  };

  // Helper function to get access token for API calls
  const getAccessToken = async () => {
    const account = accounts[0];
    if (!account) return null;

    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['User.Read'],
        account: account
      });
      return response.accessToken;
    } catch (error) {
      console.error('Failed to acquire token:', error);
      return null;
    }
  };

  const registerTenantAndUser = async (tenantId: string, account: any) => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      // Extract domain from email
      const domain = account.username?.split('@')[1] || tenantId;
      
      const response = await fetch('/api/register-tenant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          tenantId,
          userId: account.localAccountId,
          email: account.username,
          name: account.name,
          domain: domain,
        }),
      });
      
      if (response.ok) {
        console.log('Tenant and user registered successfully');
      }
    } catch (error) {
      console.error('Failed to register tenant:', error);
    }
  };

  const loadUserRole = async (userId: string, tenantId: string) => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      setLoadingRole(true);
      console.log('[loadUserRole] Fetching role for userId:', userId, 'tenantId:', tenantId);
      const response = await fetch(`/api/user/${userId}/${tenantId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('[loadUserRole] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[loadUserRole] Response data:', data);
        console.log('[loadUserRole] User role:', data.user?.role);
        setUserRole(data.user?.role || 'User');
      } else {
        const errorText = await response.text();
        console.error('[loadUserRole] Failed with status:', response.status, 'Error:', errorText);
        setUserRole('User');
      }
    } catch (error) {
      console.error('[loadUserRole] Failed to load user role:', error);
      setUserRole('User');
    } finally {
      setLoadingRole(false);
      console.log('[loadUserRole] Final userRole state will be set');
    }
  };

  const loadTenantInfo = async (tenantId: string) => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(`/api/tenant/${tenantId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTenant(data.tenant);
      }
    } catch (error) {
      console.error('Failed to load tenant info:', error);
    }
  };

  const loadSignatures = async (tenantId: string) => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(`/api/signatures/${tenantId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSignatures(data.signatures || []);
      }
    } catch (error) {
      console.error('Failed to load signatures:', error);
    }
  };

  const handleSaveSignature = async (name: string, html: string) => {
    if (!isAuthenticated || accounts.length === 0) return;
    
    const token = await getAccessToken();
    if (!token) return;

    const tenantId = accounts[0].tenantId;
    const signatureId = currentSignature?.id || `sig-${Date.now()}`;
    
    try {
      const response = await fetch('/api/signatures', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          tenantId,
          signatureData: {
            signatureId,
            name,
            html,
            assignments: currentSignature?.assignments || []
          }
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const savedSignature = data.signature;
        
        if (currentSignature) {
          // Update existing signature
          setSignatures(signatures.map(sig => 
            sig.id === currentSignature.id 
              ? { 
                  id: savedSignature.id, 
                  name: savedSignature.name, 
                  html: savedSignature.html,
                  assignments: savedSignature.assignments
                }
              : sig
          ));
          setCurrentSignature({ 
            id: savedSignature.id, 
            name: savedSignature.name, 
            html: savedSignature.html,
            assignments: savedSignature.assignments
          });
        } else {
          // Add new signature
          const newSig = { 
            id: savedSignature.id, 
            name: savedSignature.name, 
            html: savedSignature.html,
            assignments: savedSignature.assignments
          };
          setSignatures([...signatures, newSig]);
          setCurrentSignature(newSig);
        }
        
        // Return to signature detail view after saving
        setCurrentView('signatureDetail');
      } else {
        // Handle error response
        const errorData = await response.json();
        setSaveError(errorData.error || 'Failed to save signature. Please try again.');
      }
    } catch (error) {
      console.error('Failed to save signature:', error);
      setSaveError('An error occurred while saving the signature. Please try again.');
    }
  };

  const handleDeleteSignature = async (id: string) => {
    if (!isAuthenticated || accounts.length === 0) return;
    
    const token = await getAccessToken();
    if (!token) return;

    const tenantId = accounts[0].tenantId;
    
    try {
      const response = await fetch(`/api/signatures/${tenantId}/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSignatures(signatures.filter(sig => sig.id !== id));
        setCurrentSignature(null);
        setCurrentView('signatureList');
      }
    } catch (error) {
      console.error('Failed to delete signature:', error);
    }
  };

  const handleSaveAssignments = async (groupIds: string[]) => {
    if (!isAuthenticated || accounts.length === 0 || !currentSignature) return;
    
    const tenantId = accounts[0].tenantId;
    console.log('handleSaveAssignments called', { tenantId, signatureId: currentSignature.id, groupIds });
    
    // Get access token for Graph API
    const getAccessToken = async () => {
      try {
        const response = await instance.acquireTokenSilent({
          scopes: ['User.Read.All', 'Group.Read.All'],
          account: accounts[0]
        });
        return response.accessToken;
      } catch (error) {
        console.error('Error getting access token:', error);
        return null;
      }
    };

    const accessToken = await getAccessToken();
    if (!accessToken) {
      console.error('No access token available');
      return;
    }

    // Determine which groups were added and removed
    // Use originalAssignments to compare, since currentSignature.assignments is updated in real-time
    const previousGroups = originalAssignments;
    const addedGroups = groupIds.filter(id => !previousGroups.includes(id));
    const removedGroups = previousGroups.filter(id => !groupIds.includes(id));
    
    console.log('Group changes:', { previousGroups, addedGroups, removedGroups });

    try {
      // Sync added groups
      for (const groupId of addedGroups) {
        console.log('Fetching members for added group:', groupId);
        const membersResponse = await fetch(`/api/graph/groups/${groupId}/members`, {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        
        if (membersResponse.ok) {
          const { members } = await membersResponse.json();
          console.log(`Found ${members.length} members in group ${groupId}, syncing...`);
          
          const syncResponse = await fetch('/api/sync-group-members', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({ tenantId, signatureId: currentSignature.id, groupMembers: members })
          });
          
          const syncResult = await syncResponse.json();
          console.log('Sync result:', syncResult);
        } else {
          console.error('Failed to fetch members:', membersResponse.status);
        }
      }

      // Unsync removed groups
      for (const groupId of removedGroups) {
        console.log('Fetching members for removed group:', groupId);
        const membersResponse = await fetch(`/api/graph/groups/${groupId}/members`, {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        
        if (membersResponse.ok) {
          const { members } = await membersResponse.json();
          console.log(`Found ${members.length} members in group ${groupId}, unsyncing...`);
          
          const unsyncResponse = await fetch('/api/unsync-group-members', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({ tenantId, signatureId: currentSignature.id, groupMembers: members })
          });
          
          const unsyncResult = await unsyncResponse.json();
          console.log('Unsync result:', unsyncResult);
        } else {
          console.error('Failed to fetch members:', membersResponse.status);
        }
      }

      console.log('Updating signature with new assignments...');
      // Update signature with new assignments
      const response = await fetch('/api/signatures', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          tenantId,
          signatureData: {
            signatureId: currentSignature.id,
            name: currentSignature.name,
            html: currentSignature.html,
            assignments: groupIds
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        const updatedSignature = data.signature;
        
        setSignatures(signatures.map(sig => 
          sig.id === currentSignature.id ? updatedSignature : sig
        ));
        setCurrentSignature(updatedSignature);
        setCurrentView('signatureDetail');
      }
    } catch (error) {
      console.error('Failed to save assignments:', error);
      throw error;
    }
  };

  const handleTileClick = (tile: string, tab?: SettingsTab) => {
    if (tile === 'signatures') {
      setCurrentView('signatureList');
    } else if (tile === 'settings') {
      if (tab) setSettingsTab(tab);
      setCurrentView('settings');
    } else if (tile === 'overview') {
      setCurrentView('overview');
    }
  };

  const handleNewSignature = async () => {
    if (!isAuthenticated || accounts.length === 0) return;
    
    const token = await getAccessToken();
    if (!token) return;

    const tenantId = accounts[0].tenantId;
    
    try {
      // Check signature limit before allowing creation
      const response = await fetch(`/api/signatures/${tenantId}/check-limit`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (!data.canCreate) {
          setSaveError(data.error);
          return;
        }
        // Limit check passed, proceed to editor
        setCurrentSignature(null);
        setCurrentView('signatureEditor');
      }
    } catch (error) {
      console.error('Failed to check signature limit:', error);
      // Allow creation on error to avoid blocking users
      setCurrentSignature(null);
      setCurrentView('signatureEditor');
    }
  };

  const handleSelectSignature = (signature: { id: string; name: string; html: string; assignments?: string[] }) => {
    setCurrentSignature(signature);
    setCurrentView('signatureDetail');
  };

  const handleEditSignature = () => {
    setCurrentView('signatureEditor');
  };

  const handleShowAssignments = () => {
    setOriginalAssignments(currentSignature?.assignments || []);
    setCurrentView('signatureAssignment');
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
  };

  const handleBackToList = () => {
    setCurrentView('signatureList');
  };

  const handleBackToDetail = () => {
    setCurrentView('signatureDetail');
  };

  const calculateTrialDaysRemaining = () => {
    if (!tenant?.trialExpiration) return 30; // Default to 30 if no expiration date
    
    const expirationDate = new Date(tenant.trialExpiration);
    const now = new Date();
    const diffMs = expirationDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays); // Don't show negative days
  };

  const isTrialExpired = () => {
    if (!tenant?.trial || !tenant?.trialExpiration) return false;
    
    const expirationDate = new Date(tenant.trialExpiration);
    const now = new Date();
    
    return now > expirationDate;
  };

  return (
    <div className="App">
      {!isAuthenticated ? (
        <div className="landing-page">
          <div className="landing-header">
            <img 
              src="https://stampraddin.blob.core.windows.net/images/stampr_website_header.png" 
              alt="Stampr" 
              style={{ height: '85px' }}
            />
          </div>
          <h1 className="hero-tagline">
            Gain control over your organization's signatures and unify your brand.
          </h1>
          <div className="landing-content">
            <div className="glass-box">
              <div className="sign-in-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 17L15 12L10 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M15 12H3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h1>Sign in</h1>
              <p className="landing-subtitle"></p>
              <AuthButton />
            </div>
          </div>
        </div>
      ) : (
        <>
          {tenant?.trial && (
            <div className="trial-banner">
              ⚠ This is the trial version of Stampr. Trial expires in {calculateTrialDaysRemaining()} days.
            </div>
          )}
          <header className="app-header">
            <div className="header-content">
              <div className="header-left">
                <img 
                  src="https://stampraddin.blob.core.windows.net/images/stampr_website_header.png" 
                  alt="Stampr" 
                  style={{ height: '85px', marginBottom: '0.5rem', cursor: 'pointer' }}
                  onClick={handleBackToDashboard}
                  title="Go to Dashboard"
                />
                {/* <p className="subtitle">Create and manage professional email signatures</p> */}
              </div>
              <div className="header-right">
                <AuthButton />
              </div>
            </div>
          </header>

          <div className="app-main">
            {loadingRole ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading...</p>
              </div>
            ) : tenant?.enabled === false ? (
              <div className="access-denied">
                <div className="access-denied-content">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="#721c24" strokeWidth="2"/>
                    <path d="M8 8L16 16M16 8L8 16" stroke="#721c24" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <h2>Account Disabled</h2>
                  <p>Your account has been disabled. If you have any questions or did not expect this page, please email us at <a href="mailto:helpdesk@ryantechinc.com" style={{ color: '#1A7A85', textDecoration: 'none', fontWeight: 600 }}>helpdesk@ryantechinc.com</a></p>
                </div>
              </div>
            ) : isTrialExpired() ? (
              <div className="access-denied">
                <div className="access-denied-content">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="#ff5722" strokeWidth="2"/>
                    <path d="M12 8V12" stroke="#ff5722" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="12" cy="16" r="1" fill="#ff5722"/>
                  </svg>
                  <h2>Trial Expired</h2>
                  <p>Your 30-day trial has expired. Please subscribe to continue using Stampr.</p>
                  <button className="btn-settings" onClick={() => setCurrentView('settings')}>
                    Go to Settings to Subscribe
                  </button>
                </div>
                {currentView === 'settings' && accounts.length > 0 && (
                  <Settings
                    tenantId={accounts[0].tenantId}
                    onBack={handleBackToDashboard}
                    initialTab={settingsTab}
                  />
                )}
              </div>
            ) : userRole !== 'TenantAdmin' ? (
              <div className="access-denied">
                <div className="access-denied-content">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="#1A7A85" strokeWidth="2"/>
                    <path d="M12 8V12" stroke="#1A7A85" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="12" cy="16" r="1" fill="#1A7A85"/>
                  </svg>
                  <h2>Access Restricted</h2>
                  <p>You do not have access to the Admin portal. If you need access, please request access from your administrator.</p>
                </div>
              </div>
            ) : (
              <>
                {currentView === 'dashboard' && (
                  <Dashboard 
                    onTileClick={handleTileClick} 
                    userName={accounts[0]?.name || accounts[0]?.username}
                    tenantId={accounts[0]?.tenantId}
                  />
                )}

                {currentView === 'signatureList' && (
                  <SignatureList
                    signatures={signatures}
                    onSelectSignature={handleSelectSignature}
                    onNewSignature={handleNewSignature}
                    onBack={handleBackToDashboard}
                  />
                )}

                {currentView === 'signatureDetail' && currentSignature && (
                  <SignatureDetail
                    signature={currentSignature}
                    onBack={handleBackToList}
                    onEdit={handleEditSignature}
                    onAssign={handleShowAssignments}
                    onDelete={handleDeleteSignature}
                  />
                )}

                {currentView === 'signatureEditor' && (
                  <div className="editor-view">
                    <div className="editor-header">
                      <button className="btn-back" onClick={currentSignature ? handleBackToDetail : handleBackToList}>
                        ← Back
                      </button>
                    </div>
                    <SignatureEditor
                      signature={currentSignature}
                      onSave={handleSaveSignature}
                    />
                  </div>
                )}

                {currentView === 'signatureAssignment' && currentSignature && (
                  <SignatureAssignment
                    signatureId={currentSignature.id}
                    assignments={currentSignature.assignments || []}
                    onAssignmentChange={(groups) => {
                      setCurrentSignature({ ...currentSignature, assignments: groups });
                    }}
                    onBack={handleBackToDetail}
                    onSave={handleSaveAssignments}
                  />
                )}

                {currentView === 'overview' && accounts.length > 0 && (
                  <Overview
                    tenantId={accounts[0].tenantId}
                    onBack={handleBackToDashboard}
                  />
                )}

                {currentView === 'settings' && accounts.length > 0 && (
                  <Settings
                    tenantId={accounts[0].tenantId}
                    onBack={handleBackToDashboard}
                    initialTab={settingsTab}
                  />
                )}
              </>
            )}
          </div>
        </>
      )}

      {saveError && (
        <div className="sync-modal-overlay" onClick={() => setSaveError(null)}>
          <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sync-error">
              <div className="error-icon">⚠️</div>
              <p>{saveError}</p>
              <button className="btn-close-modal" onClick={() => setSaveError(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
